import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  OnChanges,
  output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  ChannelUpdatePayload,
  DeviceChannel,
  DeviceChannelConfiguration,
  DeviceConfigurationForDeviceChannel,
  DeviceModel,
} from '@core/models';
import { XEMEX_OID } from '@core/constants';
import { TranslateModule } from '@ngx-translate/core';
import { DeviceConfigurationDetailsDialogComponent } from '../device-configuration-details-dialog/device-configuration-details-dialog.component';
import { DeviceConfigurationClearDialogComponent } from '../clear-device-configuration-dialog/clear-device-configuration-dialog.component';
import { ChangeInitialConfigurationDialogComponent } from '../change-initial-configuration-dialog/change-initial-configuration-dialog.component';
import { DeviceConfigFileDetailsDialogComponent } from '@root/app/pages/settings/containers/device-config-files/device-config-file-details-dialog/device-config-file-details-dialog.component';
import { InitialConfigFileDetailsDialogComponent } from '@root/app/pages/settings/containers/initial-config-files/initial-config-file-details-dialog/initial-config-file-details-dialog.component';
import { UserStore } from '@root/app/store/user.store';
import { MatButtonModule } from '@angular/material/button';
import { DeviceVerifications, VerificationAttempt } from '@core/models/verification.model';
import { MatPaginatorModule } from '@angular/material/paginator';
import { DeviceVerificationDetailsDialogComponent } from '../device-verification-dialog/device-verification-details-dialog.component';
import { SpinnerComponent } from '@custom-components/general/spinner/spinner.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ChannelConfigurationErrorReason, DeviceStore } from '@root/app/store/device.store';
import { CardComponent } from '@custom-components/general/card/card.component';
import { CardGridComponent } from '@custom-components/general/card-grid';
import {
  StatusChipComponent,
  StatusBadgeType,
} from '@custom-components/general/status-chip/status-chip.component';
import { DetailFieldComponent } from '@custom-components/general/detail-field';

@Component({
  selector: 'xemex-device-configuration-tab',
  imports: [
    CommonModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    TranslateModule,
    MatTableModule,
    MatPaginatorModule,
    SpinnerComponent,
    MatProgressSpinnerModule,
    CardComponent,
    CardGridComponent,
    StatusChipComponent,
    DetailFieldComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './device-configuration-tab.component.html',
})
export class DeviceConfigurationTabComponent implements OnChanges {
  verificationPageChange = output<{ pageIndex: number; pageSize: number }>();
  viewAllVerifications = output<void>();
  currentVerificationPage = 0;
  verificationPageSize = 10;
  isPerformingVerification = false;
  permissions = input<string[]>([]);
  deviceDetail = input<DeviceModel>();
  isLoadingConfiguration = input<boolean>(false);
  channelConfigurationError = input<ChannelConfigurationErrorReason>();
  isLoadingVerification = input<boolean>(false);
  dataSource = new MatTableDataSource<DeviceChannel>([]);
  initialConfigDataSource = new MatTableDataSource<{ description: string | null }>([]);
  verificationDataSource = new MatTableDataSource<VerificationAttempt>([]);
  verifications = input<DeviceVerifications>();
  channelConfiguration = input<DeviceChannelConfiguration>();
  deviceConfigurations = input<DeviceConfigurationForDeviceChannel[]>([]);
  channelChanged = output<ChannelUpdatePayload>();
  verificationColumns: string[] = ['initiatedOn', 'initiatedBy', 'status', 'error'];

  displayedColumns: string[] = [
    'channelNumber',
    'description',
    'deviceConfiguration.installationDate',
    'action',
  ];
  readonly dialog = inject(MatDialog);
  readonly deviceStore = inject(DeviceStore);
  readonly userStore = inject(UserStore);
  deviceDialogRef: MatDialogRef<DeviceConfigurationDetailsDialogComponent> | null = null;
  deviceDialogClearRef: MatDialogRef<DeviceConfigurationClearDialogComponent> | null = null;

  isXemex = computed(() => {
    const initialPartner = this.userStore.user()?.partners?.find(p => p.is_initial_partner);
    return initialPartner?.partnerOid === XEMEX_OID;
  });

  // Verification reads out the registers of the installed RS-485 subdevices, so it is only offered
  // for a device type that supports topology at all — the start-verification endpoint rejects any
  // other with a 422. Both protocols that have subdevices are verifiable.
  private static readonly VERIFIABLE_PROTOCOLS: readonly string[] = ['EnnyLCU', 'DLMS'];

  showVerification = computed(() => {
    const device = this.deviceDetail();
    return (
      !!device?.deviceSupportsTopology &&
      DeviceConfigurationTabComponent.VERIFIABLE_PROTOCOLS.includes(device.protocol) &&
      this.permissions().includes('edit_topology_device')
    );
  });

  // Sub devices can only be added for non-DLMS devices; DLMS topology is fixed.
  canAddSubDevice = computed(() => this.deviceDetail()?.protocol !== 'DLMS');

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['channelConfiguration'] && this.channelConfiguration) {
      this.dataSource.data = this.channelConfiguration()?.channels || [];
      this.initialConfigDataSource.data = [
        {
          description: this.channelConfiguration()?.initialConfiguration?.description ?? null,
        },
      ];
    }
    if (changes['deviceConfigurations'] && this.deviceDialogRef) {
      this.deviceDialogRef.componentInstance.data.deviceConfigurations =
        this.deviceConfigurations();
    }
    if (changes['verifications'] && this.verifications) {
      this.verificationDataSource.data = this.verifications()?.verifications || [];
    }
  }

  onPageChange(pageIndex: number) {
    this.currentVerificationPage = pageIndex;
    this.verificationPageChange.emit({
      pageIndex: pageIndex,
      pageSize: this.verificationPageSize,
    });
  }

  showVerificationDetails(verification: VerificationAttempt) {
    this.dialog.open(DeviceVerificationDetailsDialogComponent, {
      width: '850px',
      maxWidth: '850px',
      height: '80vh',
      data: verification,
    });
  }

  performVerification() {
    const deviceId = this.deviceDetail()?.deviceId;
    if (deviceId) {
      this.isPerformingVerification = true;
      const startVerificationObservable = this.deviceStore.performVerication(deviceId);
      startVerificationObservable.subscribe({
        next: result => {
          this.isPerformingVerification = false;
          this.deviceStore.getVerificationAttempts({
            deviceId: +deviceId,
            pageIndex: undefined,
            pageSize: undefined,
          });
          this.showVerificationDetails({
            error: null,
            status: '',
            initiatedBy: '',
            initiatedOn: '',
            verificationId: result.verificationId,
          });
        },
        error: err => {
          this.isPerformingVerification = false;
          this.showVerificationDetails({
            error: JSON.stringify(err),
            status: 'Error',
            initiatedBy: '',
            initiatedOn: '',
            verificationId: '',
          });
        },
      });
    }
  }

  addDeviceConfigurationDialog() {
    const channel = {
      channelNumber:
        Math.max(0, ...(this.channelConfiguration()?.channels.map(c => c.channelNumber) ?? [])) + 1,
    };
    this.deviceDialogRef = this.dialog.open(DeviceConfigurationDetailsDialogComponent, {
      width: '850px',
      maxWidth: '850px',
      data: {
        channel,
        deviceConfigurations: [],
        protocol: this.deviceDetail()?.protocol,
        mode: 'add',
      },
    });

    this.deviceDialogRef.afterClosed().subscribe((payload: ChannelUpdatePayload) => {
      payload && this.channelChanged.emit(payload);
    });
  }

  editDeviceConfigurationDialog(channel: DeviceChannel) {
    this.deviceDialogRef = this.dialog.open(DeviceConfigurationDetailsDialogComponent, {
      width: '850px',
      maxWidth: '850px',
      data: {
        channel,
        deviceConfigurations: [],
        protocol: this.deviceDetail()?.protocol,
        mode: 'edit',
      },
    });

    this.deviceDialogRef.afterClosed().subscribe((payload: ChannelUpdatePayload) => {
      payload && this.channelChanged.emit(payload);
    });
  }

  openInitialConfigDetails() {
    const initialConfig = this.channelConfiguration()?.initialConfiguration;
    if (!initialConfig?.fileId) return;
    this.dialog.open(InitialConfigFileDetailsDialogComponent, {
      width: '850px',
      maxWidth: '850px',
      maxHeight: '90vh',
      panelClass: 'h-full',
      data: { fileId: initialConfig.fileId, versionId: initialConfig.versionId },
    });
  }

  openDeviceConfigDetails(channel: DeviceChannel) {
    const config = channel.deviceConfiguration;
    if (!config?.fileId) return;
    this.dialog.open(DeviceConfigFileDetailsDialogComponent, {
      width: '850px',
      maxWidth: '850px',
      maxHeight: '90vh',
      panelClass: 'h-full',
      data: { fileId: config.fileId, versionId: config.versionId },
    });
  }

  changeInitialConfigurationDialog() {
    const deviceId = this.deviceDetail()?.deviceId;
    if (!deviceId) return;

    const dialogRef = this.dialog.open(ChangeInitialConfigurationDialogComponent, {
      width: '850px',
      maxWidth: '850px',
      data: {
        deviceId,
        selectedFileId: this.channelConfiguration()?.initialConfiguration?.fileId,
      },
    });

    dialogRef.afterClosed().subscribe();
  }

  deleteDeviceConfigurationDialog(channel: DeviceChannel) {
    this.deviceDialogClearRef = this.dialog.open(DeviceConfigurationClearDialogComponent, {
      width: '850px',
      maxWidth: '850px',
      data: {
        channelNumber: channel.channelNumber,
      },
    });
  }

  private readonly CATEGORY_CONFIG: Record<string, { icon: string; bg: string; text: string }> = {
    'Energy Meter': { icon: 'bolt', bg: 'bg-green-100', text: 'text-green-600' },
    Inverter: { icon: 'solar_power', bg: 'bg-yellow-100', text: 'text-yellow-500' },
    Heatpump: { icon: 'heat_pump', bg: 'bg-blue-100', text: 'text-blue-500' },
    'EV Charger': { icon: 'ev_station', bg: 'bg-purple-100', text: 'text-purple-500' },
  };

  getCategoryIcon(category?: string): string {
    return (category && this.CATEGORY_CONFIG[category]?.icon) || 'device_hub';
  }

  getCategoryBgClass(category?: string, configured = true): string {
    if (!configured) return 'bg-gray-100';
    return (category && this.CATEGORY_CONFIG[category]?.bg) || 'bg-gray-100';
  }

  getCategoryIconClass(category?: string, configured = true): string {
    if (!configured) return 'text-gray-400';
    return (category && this.CATEGORY_CONFIG[category]?.text) || 'text-gray-500';
  }

  getVerificationChipStatus(status: string): StatusBadgeType {
    if (status === 'Success') return 'success';
    if (status === 'Failed') return 'error';
    return 'pending';
  }

  /**
   * This block only renders while `deviceDetail()?.deviceSupportsTopology` is true (see the
   * component template), which is the same flag the channel-configuration endpoint itself checks
   * before it can ever answer 404 "Device does not support channels" (`getDeviceChannels` in
   * device-func's topology service). So a `notSupported` classification can never legitimately
   * reach this switch — surfacing its own message here would claim the device "doesn't support
   * channel configuration" right next to a page that just showed it does. Treat it like any other
   * unexpected mismatch: the generic retry message, not the notSupported one.
   */
  getChannelConfigurationErrorMessageKey(): string {
    if (this.channelConfigurationError() === 'notFound') {
      return 'devices.detailTabs.configuration.configurationErrorNotFound';
    }
    return 'devices.detailTabs.configuration.configurationErrorGeneric';
  }
}
